// import EventEmitter from 'eventemitter3';
import { createWorkerManager } from './worker-manager.js';
import { createStatusManager } from './status-manager.js';
import { createInitialSessionData, processAttemptResults } from './utils/measurement-processing.js';
import EventEmitter from './utils/buffered-eventemitter';

let videoFrameCallback;
let videoEl;
let countdownSetIntervalId;
let currentTimeLeft;
let vitalsHaveBeenAuthorized;
let authorizedVitals;
let inPreviewMode = false;
let guideCoordinatesSet = false;
let currentAttemptSignsMsgs = {}; // Collect signsMessage events during session
let captureTimeUsed = false;
let stopFuncCalled = false;

let remainingSessionAttempts = 3;
let vitalsSessionData = {};
let finalVitalsMeasurementValues = {};
let vitalsMeasurementsAllAttempts = [];
let signsMessagesAllAttempts = [];
let metadataAllAttempts = [];

let imageWidth;
let imageHeight;
let imageXOffset;
let imageYOffset;
let rawVideoDimensions;
let coreVideoDimensions;

const statusWarningMessageMap = {
  'E-DQM-021': { message: 'Please stay still', code: 'E-DQM-021', severity: 'warning' },
  'E-DQM-022': { message: 'Improve your lighting so your face is evenly lit', code: 'E-DQM-022', severity: 'warning' },
  'E-FDM-041': { message: 'Face not detected', code: 'E-FDM-041', severity: 'warning' },
  'E-FDM-042': { message: 'Please keep face centered', code: 'E-FDM-042', severity: 'warning' },
  'W-RES-073': { message: 'Please keep face centered', code: 'W-RES-073', severity: 'warning' },
  'W-RES-076': { message: 'Please keep face centered', code: 'W-RES-076', severity: 'warning' },
  'W-RES-078': { message: 'Please keep face centered', code: 'W-RES-078', severity: 'warning' },
  'W-RES-079': { message: 'Face too far from camera', code: 'W-RES-079', severity: 'warning' },
  'W-RES-080': { message: 'Face too close to camera', code: 'W-RES-080', severity: 'warning' },
};

const processDataStatus = (dataStatusArr) => {
  return dataStatusArr.map((statusCode) => {
    if (statusWarningMessageMap[statusCode]) {
      return statusWarningMessageMap[statusCode];
    }

    return { code: statusCode, message: '', severity: 'info' };
  });
}

/**
 * Creates a Vital Client - Primary SDK interface
 * @param {Object} config - Configuration object
 * @param {string} config.workerDirectory - Path to worker directory
 * @param {Function} config.authenticator - Callback function(runToken) that returns auth response
 */
export function createVitalClient(config = {}) {
  const emitter = new EventEmitter();
  const clientConfig = {
    ...config,
  };

  let workerManager = null;
  const statusManager = createStatusManager();
  let initialized = false;
  let startTimestamp;
  let maxSessionTime = 0;

  let brEarlyEndEnabled = false;
  let hrEarlyEndEnabled = false;
  let bpEarlyEndEnabled = false;
  let spo2EarlyEndEnabled = false;
  let brCanEndEarly = false;
  let hrCanEndEarly = false;
  let bpCanEndEarly = false;
  let spo2CanEndEarly = false;

  // Frame counting
  let framesSent = 0;
  let framesProcessed = 0;
  let bytesSent = 0;
  let frameMode = 'idle';
  let sessionCount = 0;
  let bitmapCreateMs = 0;
  let bitmapCreateMax = 0;
  let bitmapCreateSum = 0;

  /**
   * Initialize the SDK and worker
   */
  const init = async () => {
    if (initialized) {
      throw new Error('VitalClient already initialized');
    }

    workerManager = createWorkerManager(clientConfig);

    // Forward worker events
    workerManager.on('ready', () => {
      initialized = true;
      statusManager.updateStatus({ initialized: true });
      emitter.emit('ready');
    });

    workerManager.on('error', (error) => {
      emitter.emit('error', error);
    });

    workerManager.on('log', (msg) => {
      emitter.emit('log', msg);
    });

    workerManager.on('signsMessage', (msg) => {
      framesProcessed++;
      handleSignsMsgs(msg.data, msg.duration, msg.totalDuration, msg.imageDataDuration);
    });

    workerManager.on('segmentationDuration', (data) => {
      emitter.emit('segmentationDuration', data);
    });

    workerManager.on('faceDetectionDuration', (data) => {
      emitter.emit('faceDetectionDuration', data);
    });

    try {
      await workerManager.init();
    } catch (e) {
      workerManager.destroy();
      throw e;
    }
  };

  /**
   * Processes message from the vital core algorithm to determine when to end
   * a session attempt
   */
  const handleSignsMsgs = async (msg, duration, totalDuration, imageDataDuration) => {
    const { dataStatus } = msg;

    // Store message with timestamp (matches vital-trac-app pattern)
    const currTimestamp = Date.now();
    currentAttemptSignsMsgs[currTimestamp] = msg;

    if (dataStatus && dataStatus.includes('S-HRT-061')) {
      hrCanEndEarly = true;
    }

    if (dataStatus && dataStatus.includes('S-RES-070')) {
      brCanEndEarly = true;
    }

    if (dataStatus && dataStatus.includes('S-BPR-090')) {
      bpCanEndEarly = true;
    }

    if (dataStatus && dataStatus.includes('S-SPO-100')) {
      spo2CanEndEarly = true;
    }

    const msgClone = { ...msg, duration, totalDuration, imageDataDuration };
    if (msg.dataStatus) {
      msgClone.dataStatus = processDataStatus(msg.dataStatus);
    }

    emitter.emit('signsMessage', msgClone);

    if (canEndEarly() || (Date.now() - startTimestamp > (maxSessionTime * 1000))) {
      await stop();
    }
  };

  /**
   * Determines if results are in for all vitals being measured and a session attempt can end
   * early before the max session time has elapsed
   */
  const canEndEarly = () => {
    if (!hrEarlyEndEnabled && !brEarlyEndEnabled && !bpEarlyEndEnabled && !spo2EarlyEndEnabled) {
      return false;
    }

    let enabledVitalsCollected = true;

    if (hrEarlyEndEnabled) {
      enabledVitalsCollected &= hrCanEndEarly;
    }
    if (brEarlyEndEnabled) {
      enabledVitalsCollected &= brCanEndEarly;
    }
    if (bpEarlyEndEnabled) {
      enabledVitalsCollected &= bpCanEndEarly;
    }
    if (spo2EarlyEndEnabled) {
      enabledVitalsCollected &= spo2CanEndEarly;
    }

    return enabledVitalsCollected;
  }

  /**
   * Set configuration to determine if a session attempt can end early if results for all
   * vitals are ready before the max session time has elapsed
   */
  const setEarlyEndEnabled = (selectedVitals, authorizedVitals) => {
    const selectedSet = new Set(selectedVitals);
    const authorizedSet = new Set(authorizedVitals);

    hrEarlyEndEnabled = (authorizedSet.has('PR_MD') ^ authorizedSet.has('PR_GW')) && (selectedSet.has('PR_MD') || selectedSet.has('PR_GW'));
    brEarlyEndEnabled = (authorizedSet.has('RR_MD') ^ authorizedSet.has('RR_GW')) && (selectedSet.has('RR_MD') || selectedSet.has('RR_GW'));
    bpEarlyEndEnabled = (authorizedSet.has('BP_MD') ^ authorizedSet.has('BP_GW')) && (selectedSet.has('BP_MD') || selectedSet.has('BP_GW'));
    spo2EarlyEndEnabled = (authorizedSet.has('SPO2_MD') ^ authorizedSet.has('SPO2_GW')) && (selectedSet.has('SPO2_MD') || selectedSet.has('SPO2_GW'));
  }

  const setImageDimensions = (videoEl, zoomedIn) => {
    if (videoEl.videoWidth > videoEl.videoHeight) {
      imageWidth = 640;
      imageHeight = 480;
    } else {
      imageWidth = 480;
      imageHeight = 640;
    }

    if (zoomedIn) {
      imageXOffset = Math.round((videoEl.videoWidth - imageWidth) / 2);
      imageYOffset = Math.round((videoEl.videoHeight - imageHeight) / 2);
    } else {
      imageXOffset = 0;
      imageYOffset = 0;
    }

    coreVideoDimensions = `${imageWidth}x${imageHeight}`;
    rawVideoDimensions = `${videoEl.videoWidth}x${videoEl.videoHeight}`;
  }

  /**
   * Ported over from vital-trac. Returns size and positioning of guide. These values are used
   * to instruct vital core where patients should be located in the images passed to vital core
   * @param {HTMLVideoElement} videoEl - html video element used to determin video orientation
   * @returns {Object} coordinates of guide
   */
  const getGuideCoordinates = (videoEl) => {
    // Percentages (0-1)
    let videoWidth = 480;
    let videoHeight = 640;

    if (videoEl.videoWidth > videoEl.videoHeight) {
      videoWidth = 640;
      videoHeight = 480;
    }

    const wPct = 0.5; // Width of the box is half the width of canvas
    const hPct = 0.5; // Height of the box is half the height of canvas
    const xPct = 0.0; // 0.0 is center, -0.1 is left of center
    const yPct = -0.1; // 0.0 is center, -0.1 is above center
    const width = Math.min(videoWidth, videoHeight) * wPct;
    const height = Math.max(videoWidth, videoHeight) * hPct;
    const x = videoWidth / 2 + xPct * videoWidth - width / 2;
    const y = videoHeight / 2 + yPct * videoHeight - height / 2;

    return { x, y, width, height };
  }

  /**
   * [Start] measurement session
   */
  const start = async (sessionConfig, videoEl, zoomedIn) => {
    if (!initialized) {
      throw new Error('VitalClient not initialized. Call init() first.');
    }
    if (!vitalsHaveBeenAuthorized) {
      throw new Error('Vital Core Algorithm has not been authorized');
    }
    if (!videoEl || !(videoEl instanceof HTMLVideoElement)) {
      throw new Error('A video element is required by the start() function')
    }
    if (inPreviewMode) {
      await stopPreviewMode();
    }

    setImageDimensions(videoEl, zoomedIn);

    stopFuncCalled = false;

    if (!guideCoordinatesSet) {
      const guideCoordinates = getGuideCoordinates(videoEl);
      await workerManager.callMethod('setFaceGuide', guideCoordinates);
      emitter.emit('guideCoordinates', guideCoordinates);
      guideCoordinatesSet = true;
    }

    let selectedVitals;

    if (remainingSessionAttempts === 0) {
      throw new Error('No remaining session attempts left');
    } else if (remainingSessionAttempts === 3) {
      selectedVitals = sessionConfig.vitals;
      vitalsSessionData = createInitialSessionData(sessionConfig.vitals);
    } else {
      selectedVitals = Object.entries(vitalsSessionData).reduce((vitalsToMeasure, [vital, vitalData]) => {
        if (vitalData.needToMeasure) {
          vitalsToMeasure.push(vital);
        }
        return vitalsToMeasure;
      }, []);
    }

    brCanEndEarly = false;
    hrCanEndEarly = false;
    bpCanEndEarly = false;
    spo2CanEndEarly = false;

    // Reset frame counters and signs messages collection for new session
    framesSent = 0;
    framesProcessed = 0;
    bytesSent = 0;
    bitmapCreateMs = 0;
    bitmapCreateMax = 0;
    frameMode = 'session';
    sessionCount++;
    currentAttemptSignsMsgs = {};

    setEarlyEndEnabled(selectedVitals, authorizedVitals);

    const sessionTags = JSON.stringify(sessionConfig);
    await workerManager.callMethod('startSession', sessionTags);

    startCountdownLoop();
    startTimestamp = Date.now();
    statusManager.updateStatus({ measuring: true });
    emitter.emit('start', { selectedVitals });
    if (!videoFrameCallback) {
      ingestFrames(videoEl);
    }
  };

  /**
   * Start preview mode
   */
  const startPreviewMode = async (videoEl, zoomedIn = false) => {
    if (!initialized) {
      throw new Error('VitalClient not initialized');
    }

    setImageDimensions(videoEl, zoomedIn);

    const guideCoordinates = getGuideCoordinates(videoEl);
    await workerManager.callMethod('setFaceGuide', guideCoordinates);
    emitter.emit('guideCoordinates', guideCoordinates);
    guideCoordinatesSet = true;

    try {
      await workerManager.callMethod('previewSession');
      inPreviewMode = true;
      framesSent = 0;
      framesProcessed = 0;
      bytesSent = 0;
      bitmapCreateMs = 0;
      bitmapCreateMax = 0;
      bitmapCreateSum = 0;
      frameMode = 'preview';
      ingestFrames(videoEl);
      emitter.emit('previewModeStart');
    } catch (e) {
      console.log('preview mode failed to start', e);
      throw new Error('Preview mode failed to start');
    }
  }

  /**
   * Stop preview mode
   */
  const stopPreviewMode = async () => {
    try {
      videoEl.cancelVideoFrameCallback(videoFrameCallback);
      videoFrameCallback = null;
      await workerManager.callMethod('endSession');
      inPreviewMode = false;
      emitter.emit('previewModeStop');
    } catch {
      throw new Error('Preview mode failed to stop');
    }
  }

  /**
   * Stop measurement session
   */
  const stop = async () => {
    if (!initialized) {
      throw new Error('VitalClient not initialized');
    }

    if (stopFuncCalled === true) return;

    stopFuncCalled = true;
    const timestamp = Date.now();
    videoEl.cancelVideoFrameCallback(videoFrameCallback);
    videoFrameCallback = null;

    let wasmResult = await workerManager.callMethod('endSession');
    wasmResult = JSON.parse(wasmResult);
    startTimestamp = null;

    // Format result to match vital-trac-app structure
    // This makes it ready for VITAL-TRAC PRO submission
    const vitalMeasurements = wasmResult.vitals || {};

    // Map BP results - supports both regressor (sbp_mmHg/dbp_mmHg) and classifier (hypertensive/confidence) formats
    for (const bpTag of ['BP_GW', 'BP_MD']) {
      const bp = vitalMeasurements[bpTag];
      if (!bp) continue;
      if (bp.sbp_mmHg && bp.dbp_mmHg) {
        bp.BP_SYS = bp.sbp_mmHg;
        bp.BP_DIA = bp.dbp_mmHg;
      } else if (bp.hypertensive !== undefined && bp.confidence !== undefined) {
        bp.classification = bp.hypertensive ? 'Hypertensive' : 'Normal';
      }
    }

    const prevFinalVitalsMeasurementValues = finalVitalsMeasurementValues || {};

    const processedAttemptResults = processAttemptResults(vitalMeasurements, vitalsSessionData, prevFinalVitalsMeasurementValues);
    const {
      sessionComplete,
      updatedVitalsData,
      updatedFinalValues,
    } = processedAttemptResults;

    vitalsSessionData = { ...vitalsSessionData, ...updatedVitalsData };
    finalVitalsMeasurementValues = updatedFinalValues;
    remainingSessionAttempts = sessionComplete ? 0 : remainingSessionAttempts - 1;
    vitalsMeasurementsAllAttempts = [vitalMeasurements, ...vitalsMeasurementsAllAttempts]
    signsMessagesAllAttempts = [currentAttemptSignsMsgs, ...signsMessagesAllAttempts];
    metadataAllAttempts = [wasmResult.metadata, ...metadataAllAttempts];

    const result = {
      vitals: {
        vitals: vitalsMeasurementsAllAttempts[0] // Double-nested structure for VITAL-TRAC
      },
      signsMsgs: signsMessagesAllAttempts[0], // All collected signs messages
      prevVitals: vitalsMeasurementsAllAttempts.slice(1),
      prevSignsMsgs: signsMessagesAllAttempts.slice(1),
      finalVitalsMeasurementValues, // Required for score display
      noValidMeasurements: false,
      webAppVersion: '1.0.0',
      frameCollectionMethod: 'web-sdk',
      metadata: metadataAllAttempts[0],
      prevMetadata: metadataAllAttempts.slice(1),
      timestamp,
      captureTimeUsed,
      rawVideoDimensions,
      coreVideoDimensions,
    };

    const resultData ={
      result,
      sessionComplete,
      remainingSessionAttempts,
    };

    frameMode = 'idle';
    statusManager.updateStatus({ measuring: false });
    clearInterval(countdownSetIntervalId);
    emitter.emit('stop', resultData);
    emitter.emit('timeLeft', 0);

    return resultData;
  };

  /**
   * Ingest video frames for processing
   */
  const ingestFrames = (videoElement) => {
    if (!initialized) {
      throw new Error('VitalClient not initialized');
    }

    videoEl = videoElement;

    const onVideoFrameCallback = async (_now, metadata) => {
      let timestamp;
      const t0 = performance.now();
      if ('captureTime' in metadata) {
        timestamp = Math.round(metadata.captureTime);
        if (!captureTimeUsed) captureTimeUsed = true;
      } else {
        timestamp = Date.now();
      }

      const imgBitmap = await createImageBitmap(videoEl, imageXOffset, imageYOffset, imageWidth, imageHeight);
      const dur = performance.now() - t0;
      bitmapCreateMs = dur;
      bitmapCreateSum += dur;
      if (dur > bitmapCreateMax) bitmapCreateMax = dur;

      framesSent++;
      bytesSent += videoEl.videoWidth * videoEl.videoHeight * 4;
      workerManager.detectVitalsJS(imgBitmap, imageWidth, imageHeight, timestamp);
      imgBitmap.close();

      videoFrameCallback = videoEl.requestVideoFrameCallback(onVideoFrameCallback);
    };

    videoFrameCallback = videoEl.requestVideoFrameCallback(onVideoFrameCallback);
  }

  function startCountdownLoop() {
    currentTimeLeft = maxSessionTime;
    emitter.emit('timeLeft', {
      timeLeft: currentTimeLeft,
      maxTime: maxSessionTime,
      percentComplete: 0,
      percentRemaining: 1,
    });

    countdownSetIntervalId = setInterval(() => {
      currentTimeLeft--;

      const percentRemaining = currentTimeLeft / maxSessionTime;
      const percentComplete = 1 - percentRemaining;

      emitter.emit('timeLeft', {
        timeLeft: currentTimeLeft,
        maxTime: maxSessionTime,
        percentComplete,
        percentRemaining,
      });

      if (currentTimeLeft <= 0) {
        clearInterval(countdownSetIntervalId);
      }
    }, 1000);
  }

  /**
   * Get version string from worker
   */
  const getVersion = async () => {
    if (!initialized) {
      throw new Error('VitalClient not initialized');
    }
    return await workerManager.callMethod('getVersionString');
  };

  /**
   * Set face guide region
   */
  const setFaceGuide = async (faceGuide) => {
    if (!initialized) {
      throw new Error('VitalClient not initialized');
    }
    return await workerManager.callMethod('setFaceGuide', faceGuide);
  };

  /**
   * Enable/disable extended logging
   */
  const setExtendedLogs = async (enabled) => {
    if (!initialized) {
      throw new Error('VitalClient not initialized');
    }
    return await workerManager.callMethod('setExtendedLogs', enabled);
  };

  /**
   * Update SDK options
   */
  const updateOptions = (patch = {}) => {
    Object.assign(clientConfig, patch);
    // TODO: Notify worker of config changes
  };

  /**
   * Get frame counts (sent to worker vs processed by worker)
   */
  const getFrameCounts = () => {
    const bitmapCreateAvg = framesSent > 0 ? bitmapCreateSum / framesSent : 0;
    return { framesSent, framesProcessed, bytesSent, frameMode, sessionCount, bitmapCreateMs: Math.round(bitmapCreateMs * 100) / 100, bitmapCreateMax: Math.round(bitmapCreateMax * 100) / 100, bitmapCreateAvg: Math.round(bitmapCreateAvg * 100) / 100 };
  };

  /**
   * Get current status
   */
  const getStatus = () => {
    return statusManager.getStatus();
  };

  /**
   * Register log handler
   */
  const onLog = (handler) => {
    if (workerManager) {
      workerManager.onLog(handler);
    }
  };

  /**
   * Unregister log handler
   */
  const offLog = () => {
    if (workerManager) {
      workerManager.offLog();
    }
  };

  /**
   * Auth vital core
   */
  const authorize = async () => {
    if (!vitalsHaveBeenAuthorized) {
      try {
        if (!clientConfig.authenticator) {
          throw new Error('Authenticator callback not provided in config');
        }

        let authorizeVitalsResp;
        try {
          authorizeVitalsResp = await workerManager.authorizeVitalCore(
            clientConfig.authenticator
          );
        } catch {
          throw new Error('Unable to verify the license key to use Vital-Trac.');
        }

        let vitalsToMeasure = [];
        maxSessionTime = 0;

        if (authorizeVitalsResp.status === 'authorized') {
          if (!authorizeVitalsResp.vitals) {
            throw new Error('The user or clinic is unlicensed. Please contact an administrator to enable access to Vital-Trac.');
          } else {
            vitalsToMeasure = authorizeVitalsResp.vitals.reduce((vitalsToMeasure, vital) => {
              vitalsToMeasure.push(vital.tag);
              maxSessionTime = Math.max(vital.max_session_time, maxSessionTime);

              return vitalsToMeasure;
            }, []);
          }
        } else {
          throw new Error('There is not an active license key to use Vital-Trac.');
        }

        emitter.emit('authorizedVitals', vitalsToMeasure);
        emitter.emit('maxSessionTime', maxSessionTime);

        authorizedVitals = vitalsToMeasure;
        vitalsHaveBeenAuthorized = true;
      } catch (e) {
        console.log('error calling workingManager.authorizeVitalCore');
        throw e;
      }
    } else {
      return { authorizedVitals, maxSessionTime }
    }
  }

  /**
   * Cleanup and destroy client
   */
  const destroy = () => {
    if (workerManager) {
      workerManager.destroy();
    }
    emitter.emit('destroyed');
    emitter.removeAllListeners();
    initialized = false;
    statusManager.reset();
  };

  return {
    // Core methods
    init,
    start,
    stop,
    destroy,
    startPreviewMode,
    stopPreviewMode,

    // Worker methods
    getVersion,
    authorize,
    setExtendedLogs,

    // Logging
    onLog,
    offLog,

    // Configuration
    updateOptions,
    getStatus,
    getFrameCounts,

    // Event emitter methods
    on: emitter.on.bind(emitter),
    once: emitter.once.bind(emitter),
    off: emitter.off.bind(emitter),

    // Expose for testing and advanced usage
    get initialized() { return initialized; },
  };
}
